import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type Language = 'en' | 'id';

export interface TranslationData {
  [key: string]: string;
}

export interface LanguageContextType {
  currentLanguage: Language;
  translations: TranslationData;
  isLoading: boolean;
  error: string | null;
  changeLanguage: (language: Language) => Promise<void>;
  t: (key: string, params?: { [key: string]: string | number }) => string;
  sessionId: string | null;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

// Generate or get session ID from localStorage
const getOrCreateSessionId = (): string => {
  let sessionId = localStorage.getItem('sukoharjo_session_id');
  if (!sessionId) {
    sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('sukoharjo_session_id', sessionId);
  }
  return sessionId;
};

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState<Language>('id'); // Default to Indonesian
  const [translations, setTranslations] = useState<TranslationData>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  // Initialize session and load user preferences
  useEffect(() => {
    const initializeLanguage = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const newSessionId = getOrCreateSessionId();
        setSessionId(newSessionId);

        // Try to get user preference from backend
        const response = await fetch(`${API_BASE_URL}/translations/preferences/${newSessionId}`, {
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data.language) {
            setCurrentLanguage(data.data.language);
            // Also store in localStorage for offline fallback
            localStorage.setItem('sukoharjo_language', data.data.language);
          }
        } else {
          // Fallback to localStorage if backend is not available
          const savedLanguage = localStorage.getItem('sukoharjo_language') as Language;
          if (savedLanguage && ['en', 'id'].includes(savedLanguage)) {
            setCurrentLanguage(savedLanguage);
          } else {
            // Ensure Indonesian is default if no preference is saved
            setCurrentLanguage('id');
            localStorage.setItem('sukoharjo_language', 'id');
          }
        }
      } catch (error) {
        console.error('Error initializing language:', error);
        // Fallback to localStorage
        const savedLanguage = localStorage.getItem('sukoharjo_language') as Language;
        if (savedLanguage && ['en', 'id'].includes(savedLanguage)) {
          setCurrentLanguage(savedLanguage);
        } else {
          // Ensure Indonesian is default if no preference is saved
          setCurrentLanguage('id');
          localStorage.setItem('sukoharjo_language', 'id');
        }
      }
    };

    initializeLanguage();
  }, [API_BASE_URL]);

  // Load translations when language changes
  useEffect(() => {
    const loadTranslations = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await fetch(`${API_BASE_URL}/translations/${currentLanguage}`, {
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            setTranslations(data.data);
          } else {
            throw new Error('Invalid response format');
          }
        } else {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
      } catch (error) {
        console.error('Error loading translations:', error);
        setError('Failed to load translations. Using fallback text.');
        
        // Provide fallback translations for essential keys
        const fallbackTranslations: TranslationData = {
          'app.title': currentLanguage === 'id' ? 'Direktori Sukoharjo' : 'Sukoharjo Directory',
          'app.subtitle': currentLanguage === 'id' 
            ? 'Jelajahi tempat-tempat menarik di Kabupaten Sukoharjo' 
            : 'Explore points of interest in Sukoharjo Regency',
          'common.loading': currentLanguage === 'id' ? 'Memuat...' : 'Loading...',
          'common.error': currentLanguage === 'id' ? 'Kesalahan' : 'Error',
          'language.english': currentLanguage === 'id' ? 'Bahasa Inggris' : 'English',
          'language.indonesian': currentLanguage === 'id' ? 'Bahasa Indonesia' : 'Indonesian',
        };
        setTranslations(fallbackTranslations);
      } finally {
        setIsLoading(false);
      }
    };

    loadTranslations();
  }, [currentLanguage, API_BASE_URL]);

  // Change language function
  const changeLanguage = async (language: Language): Promise<void> => {
    try {
      setCurrentLanguage(language);
      localStorage.setItem('sukoharjo_language', language);

      // Update preference in backend if session exists
      if (sessionId) {
        await fetch(`${API_BASE_URL}/translations/preferences/${sessionId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ language }),
        });
      }
    } catch (error) {
      console.error('Error changing language:', error);
      // Language still changes locally even if backend update fails
    }
  };

  // Translation function with parameter substitution
  const t = (key: string, params?: { [key: string]: string | number }): string => {
    let translation = translations[key] || key;
    
    // Replace parameters in the translation
    if (params && typeof translation === 'string') {
      Object.entries(params).forEach(([paramKey, paramValue]) => {
        const placeholder = `{${paramKey}}`;
        translation = translation.replace(new RegExp(placeholder, 'g'), String(paramValue));
      });
    }
    
    return translation;
  };

  const contextValue: LanguageContextType = {
    currentLanguage,
    translations,
    isLoading,
    error,
    changeLanguage,
    t,
    sessionId,
  };

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook to use the language context
export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Language switcher component
interface LanguageSwitcherProps {
  className?: string;
  showLabels?: boolean;
  variant?: 'dropdown' | 'buttons';
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ 
  className = '', 
  showLabels = true, 
  variant = 'dropdown' 
}) => {
  const { currentLanguage, changeLanguage, t, isLoading } = useLanguage();

  if (variant === 'buttons') {
    return (
      <div className={`language-switcher-buttons ${className}`}>
        <button
          onClick={() => changeLanguage('id')}
          disabled={isLoading}
          className={`language-btn ${currentLanguage === 'id' ? 'active' : ''}`}
          style={{
            padding: '6px 12px',
            margin: '0 2px',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            background: currentLanguage === 'id' ? '#3b82f6' : 'white',
            color: currentLanguage === 'id' ? 'white' : '#374151',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            fontSize: '12px',
            fontWeight: '500'
          }}
        >
          🇮🇩 {showLabels && t('language.indonesian')}
        </button>
        <button
          onClick={() => changeLanguage('en')}
          disabled={isLoading}
          className={`language-btn ${currentLanguage === 'en' ? 'active' : ''}`}
          style={{
            padding: '6px 12px',
            margin: '0 2px',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            background: currentLanguage === 'en' ? '#3b82f6' : 'white',
            color: currentLanguage === 'en' ? 'white' : '#374151',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            fontSize: '12px',
            fontWeight: '500'
          }}
        >
          🇺🇸 {showLabels && t('language.english')}
        </button>
      </div>
    );
  }

  // Dropdown variant
  return (
    <select
      value={currentLanguage}
      onChange={(e) => changeLanguage(e.target.value as Language)}
      disabled={isLoading}
      className={`language-switcher ${className}`}
      style={{
        padding: '6px 10px',
        border: '1px solid #d1d5db',
        borderRadius: '4px',
        background: 'white',
        fontSize: '12px',
        cursor: isLoading ? 'not-allowed' : 'pointer'
      }}
    >
      <option value="id">🇮🇩 {showLabels && t('language.indonesian')}</option>
      <option value="en">🇺🇸 {showLabels && t('language.english')}</option>
    </select>
  );
};
